import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/Button';
import { Sparkles, Send, LogIn, Info } from 'lucide-react';
import { motion } from 'motion/react';
import { Box, Typography, Stack, Card, Link as MuiLink, Container, Divider } from '@mui/material';

export default function EmailConfirmation() {
    const location = useLocation();
    // Get email from router state, or default to placeholder if directly accessed
    const email = location.state?.email || 'user@example.com';

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
            {/* Top Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: { xs: 3, md: 6 },
                py: 3,
                bgcolor: '#f8f9fa',
            }}>
                <Stack direction="row" alignItems="center" spacing={1} component={Link} to="/" sx={{ textDecoration: 'none' }}>
                    <Box sx={{ bgcolor: '#4f46e5', p: 0.5, borderRadius: 1, display: 'flex' }}>
                        <Sparkles color="white" size={18} />
                    </Box>
                    <Typography variant="h6" fontWeight={800} letterSpacing="-0.02em" sx={{ color: '#111827', fontSize: '1.1rem' }}>
                        PostPilot AI
                    </Typography>
                </Stack>
                <Typography variant="body2" fontWeight={600} color="#64748b" sx={{ cursor: 'pointer', '&:hover': { color: '#111827' } }}>
                    Help Center
                </Typography>
            </Box>

            {/* Main Content */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    style={{ width: '100%', maxWidth: 460 }}
                >
                    <Card elevation={0} sx={{ p: { xs: 4, sm: 5 }, borderRadius: 4, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0', bgcolor: 'white', textAlign: 'center' }}>

                        <Box sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            bgcolor: '#eae8ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 3
                        }}>
                            <Send size={28} color="#4f46e5" style={{ marginLeft: '-2px' }} />
                        </Box>

                        <Typography variant="h5" fontWeight={800} sx={{ color: '#111827', mb: 1.5, letterSpacing: '-0.02em' }}>
                            Check your email
                        </Typography>

                        <Typography variant="body2" color="#475569" sx={{ mb: 4, lineHeight: 1.6 }}>
                            We've sent a confirmation link to <br />
                            <strong style={{ color: '#111827' }}>{email}</strong>. Please <MuiLink component={Link} to="/auth/reset-password" sx={{ color: '#4f46e5', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>click the link</MuiLink> to verify your account and get started.
                        </Typography>

                        <Button component={Link} to="/auth" size="lg" sx={{ width: '100%', bgcolor: '#4f46e5', color: 'white', '&:hover': { bgcolor: '#4338ca' }, py: 1.5, fontWeight: 'bold', fontSize: '1rem', mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LogIn size={18} /> Back to Login
                            </Box>
                        </Button>

                        <Typography variant="body2" color="#64748b" fontWeight={500} sx={{ mb: 4 }}>
                            Didn't receive the email? <MuiLink href="#" color="#4f46e5" fontWeight={700} underline="hover">Resend Email</MuiLink>
                        </Typography>

                        <Divider sx={{ mb: 4, borderColor: '#f1f5f9' }} />

                        {/* Info Box */}
                        <Box sx={{ display: 'flex', gap: 1.5, p: 2, bgcolor: '#f8fafc', borderRadius: 2, textAlign: 'left', alignItems: 'flex-start' }}>
                            <Box sx={{ bgcolor: '#4f46e5', color: 'white', p: 0.25, borderRadius: '50%', display: 'flex', mt: 0.25 }}>
                                <Info size={14} />
                            </Box>
                            <Typography variant="caption" color="#475569" sx={{ lineHeight: 1.5, fontWeight: 500 }}>
                                Check your spam folder if you don't see it in your inbox within a few minutes. The link will expire in 24 hours.
                            </Typography>
                        </Box>

                    </Card>
                </motion.div>
            </Box>

            {/* Footer */}
            <Box component="footer" sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="caption" fontWeight="medium" color="#94a3b8" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <span>© {new Date().getFullYear()} PostPilot AI. All rights reserved.</span>
                    <MuiLink href="#" color="inherit" underline="hover">Privacy Policy</MuiLink>
                    <MuiLink href="#" color="inherit" underline="hover">Terms of Service</MuiLink>
                </Typography>
            </Box>
        </Box>
    );
}
